import {api} from "@/shared/api";
import {ErrorResponse} from "@/shared/timr";

import {PasswordStrengthValidation, PasswordStrengthValidationConfig} from "@/features/auth/passwordStrengthValidation";
import {ReactIsland} from "@/shared/reactIsland";
import {
	mountPasswordStrengthIsland,
	type PasswordStrengthIslandCommands,
	type PasswordStrengthIslandProps,
} from "@/features/auth/passwordStrengthIsland";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";

interface ResetPasswordSettings {
	passwordStrengthValidation?: PasswordStrengthValidation;
}

export class ResetPasswordPage {
	private static ATTRIBUTE_DISABLED = "disabled";

	private readonly passwordStrengthIsland?: ReactIsland<PasswordStrengthIslandProps, PasswordStrengthIslandCommands>;
	private readonly passwordStrengthValidation?: PasswordStrengthValidationConfig;
	private readonly passwordField: JQuery;
	private readonly submitButton: JQuery;

	constructor(private config: ResetPasswordSettings) {
		this.passwordStrengthIsland = mountPasswordStrengthIsland("resetPasswordStrengthIsland", config.passwordStrengthValidation);
		this.passwordStrengthValidation = config.passwordStrengthValidation !== undefined ?
			new PasswordStrengthValidationConfig(config.passwordStrengthValidation) : undefined;
		this.passwordField = $("#newpassword1Text");
		this.submitButton = $("button[type='submit']");

		this.initPasswordStrengthValidationListeners();

		$("#resetPasswordForm").on("submit", event => this.changePassword(event));

		this.passwordField.trigger("focus");
	}

	private changePassword(event: JQuery.TriggeredEvent) {
		event.preventDefault();
		api("changePasswordWithToken.json")
			.postForm("#resetPasswordForm")
			.done((data: { mobileDevicesDisconnected: boolean }) => {
				BootstrapModal.info({
					title: "passwordForgotten.sent.header",
					message: `resetPassword.changed${data.mobileDevicesDisconnected ? ".devicesDisconnected" : ""}`,
					buttons: [{
						text: "confirmation.ok",
						action: () => {
							window.location.href = "/timr/recording/recording.html";
						},
					}]
				});
			}).fail((response: ErrorResponse) => {
			// if there are no form input errors, but there is an error when changing the password, an error dialog is opened (with a redirect when closing)
			const changePasswordFailed = response.responseJSON?.fieldErrors?.every(fe =>
				fe.code !== "changePassword.failure.passwordsDoNotMatch" && fe.code !== "changePassword.failure.disconnectDevices");
			if (changePasswordFailed) {
				FormUtil.removeAllFeedback($("#resetPasswordForm"));
				BootstrapModal.error({
					title: "dialog.error.title",
					message: response.responseJSON?.fieldErrors?.[0]?.message,
					onClose: () => {
						window.location.href = "/timr/passwordForgotten.html";
					}
				});
			}
		});
		return false;
	}

	private validatePasswordStrength(input: string) {
		if (!this.passwordStrengthValidation) {
			return;
		}

		// The checklist UI now lives in the React island; here we only gate the submit
		// button on validity (pure logic, no legacy DOM update).
		this.updateSubmitButtonState(this.passwordStrengthValidation.validate(input).isValid);
	}

	private initPasswordStrengthValidationListeners() {
		this.passwordField.on("input", (event) => {
			const target = event.target as HTMLInputElement;
			this.passwordStrengthIsland?.dispatchCommand("passwordChanged", target.value);
			this.validatePasswordStrength(target.value);
		});
	}

	private updateSubmitButtonState(enabled: boolean) {
		if (enabled) {
			this.submitButton.removeAttr(ResetPasswordPage.ATTRIBUTE_DISABLED);
		} else {
			this.submitButton.attr(ResetPasswordPage.ATTRIBUTE_DISABLED, "");
		}
	}
}
